export interface IbexaFigureProps {
  src: string
  alt: string
  caption?: string
  byline?: string
  credit?: string
}

export interface IbexaFigureChunk {
  type: 'figure'
  props: IbexaFigureProps
}

export interface IbexaHtmlChunk {
  type: 'html'
  html: string
}

export type IbexaBodyChunk = IbexaFigureChunk | IbexaHtmlChunk

const FIGURE_SELECTOR = 'figure.embedded-figure'

function hasOnlyFigureContent(wrapper: Element): boolean {
  const leftover = wrapper.cloneNode(true) as Element
  leftover.querySelector(FIGURE_SELECTOR)?.remove()
  return (leftover.textContent ?? '').trim() === '' && leftover.querySelector('img, table, ul, ol, iframe, video') === null
}

function readFigureProps(figure: Element): IbexaFigureProps {
  const img = figure.querySelector('img')
  const captionText = figure.querySelector('figcaption')?.textContent?.trim()
  return {
    src: img?.getAttribute('src') ?? '',
    alt: img?.getAttribute('alt') ?? '',
    caption: captionText || undefined,
  }
}

/**
 * Splits a parsed Ibexa bodytext document into an ordered list of HTML and
 * figure chunks.
 *
 * Input must already be cleaned by `cleanCorruptedAttributes` and other
 * normalization steps. The splitter assumes the markup is valid.
 *
 * Accepts a `Document` instead of a string so parsing is handled by the caller
 * DOMParser in the client, jsdom on the server
 */
export function splitIbexaBodyText(doc: Document): IbexaBodyChunk[] {
  const chunks: IbexaBodyChunk[] = []
  let accumulator: Node[] = []

  const flushHtml = () => {
    if (accumulator.length === 0) {
      return
    }
    const wrapper = doc.createElement('div')
    for (const node of accumulator) {
      wrapper.appendChild(node.cloneNode(true))
    }
    const html = wrapper.innerHTML
    if (html.trim() !== '') {
      chunks.push({ type: 'html', html })
    }
    accumulator = []
  }

  // `handleHighlights` injects markup alongside text nodes, so iterate `childNodes` rather than `children`
  for (const node of Array.from(doc.body.childNodes)) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element
      if (el.matches(FIGURE_SELECTOR)) {
        flushHtml()
        chunks.push({ type: 'figure', props: readFigureProps(el) })
        continue
      }
      const nested = el.querySelectorAll(FIGURE_SELECTOR)
      // Lifting discards the wrapper, so it is gated on the wrapper holding nothing else
      if (nested.length === 1 && hasOnlyFigureContent(el)) {
        flushHtml()
        chunks.push({ type: 'figure', props: readFigureProps(nested[0]!) })
        continue
      }
      if (nested.length > 0) {
        // Wrapper carries sibling content or multiple figures — keep it whole as
        // HTML (figure degrades to a plain image) rather than losing content
        console.warn('[ibexa-bodytext] figure wrapper not lifted (sibling content or multiple figures)', { figures: nested.length })
      }
    }
    accumulator.push(node)
  }

  flushHtml()
  return chunks
}
